import { useShow } from '@refinedev/core';
import { Spin, Tag } from 'antd';
import {
  User,
  Mail,
  Shield,
  Calendar,
  Clock,
  Building2,
  MessageSquare,
  FolderKanban,
} from 'lucide-react';
import type { FC } from 'react';

interface UserDetail {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  roles: string[];
  organization?: { id: string; name: string; plan: string };
  feedbackCount: number;
  projectCount: number;
  lastLoginAt: string;
  createdAt: string;
}

const ROLE_COLORS: Record<string, { color: string; text: string }> = {
  ROLE_ADMIN: { color: 'blue', text: 'Admin' },
  ROLE_SALES: { color: 'purple', text: 'Commercial' },
  ROLE_MEMBER: { color: 'default', text: 'Membre' },
  ROLE_OWNER: { color: 'gold', text: 'Owner' },
  ROLE_USER: { color: 'default', text: 'Utilisateur' },
};

const fmtDate = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
    : '—';

const UserDetailPage: FC = () => {
  const { queryResult } = useShow<UserDetail>({ resource: 'admin/users' });
  const { data, isLoading, isError } = queryResult;
  const user = data?.data;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="fc-card p-6">
        <p className="font-medium text-red-600 dark:text-red-400">
          Impossible de charger l&apos;utilisateur.
        </p>
      </div>
    );
  }

  const roleCfg = ROLE_COLORS[user.role] ?? { color: 'default', text: user.role };
  const displayName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;

  return (
    <div className="space-y-6">
      {/* Header gradient */}
      <div className="fc-gradient-header rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3">
          <User className="h-6 w-6" />
          <div>
            <h1 className="text-xl font-bold">{displayName}</h1>
            <p className="mt-1 text-sm text-blue-100">{user.email}</p>
          </div>
        </div>
      </div>

      {/* Profil */}
      <div className="fc-card p-5">
        <h3 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Profil
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <InfoRow icon={Mail} label="Email" value={user.email} />
          <InfoRow icon={User} label="Prénom" value={user.firstName ?? '—'} />
          <InfoRow icon={User} label="Nom" value={user.lastName ?? '—'} />
          <div className="flex items-center gap-3">
            <Shield className="h-4 w-4 text-slate-400 dark:text-slate-500" />
            <span className="text-sm text-slate-500 dark:text-slate-400">Rôle</span>
            <Tag color={roleCfg.color} className="ml-auto">
              {roleCfg.text}
            </Tag>
          </div>
          <InfoRow icon={Calendar} label="Inscrit le" value={fmtDate(user.createdAt)} />
          <InfoRow icon={Clock} label="Dernière connexion" value={fmtDate(user.lastLoginAt)} />
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MiniCard
          icon={Building2}
          label="Organisation"
          value={user.organization?.name ?? '—'}
          sub={user.organization?.plan}
        />
        <MiniCard icon={MessageSquare} label="Feedbacks" value={String(user.feedbackCount ?? 0)} />
        <MiniCard icon={FolderKanban} label="Projets" value={String(user.projectCount ?? 0)} />
      </div>
    </div>
  );
};

/* ── Sub-components ─────────────────────────────── */

const InfoRow: FC<{ icon: typeof Mail; label: string; value: string }> = ({
  icon: Icon,
  label,
  value,
}) => (
  <div className="flex items-center gap-3">
    <Icon className="h-4 w-4 text-slate-400 dark:text-slate-500" />
    <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
    <span className="ml-auto text-sm font-medium text-slate-900 dark:text-white">{value}</span>
  </div>
);

const MiniCard: FC<{ icon: typeof Mail; label: string; value: string; sub?: string }> = ({
  icon: Icon,
  label,
  value,
  sub,
}) => (
  <div className="fc-card p-5 text-center">
    <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-900/30">
      <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
    </div>
    <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
      {label}
    </p>
    <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
    {sub && <Tag className="mt-2">{sub}</Tag>}
  </div>
);

export default UserDetailPage;
